if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor () {
      super()
      this.isInitializing = false
    }

    connectedCallback () {
      if (EmblaCarousel && this.carousel) {
        this.initCarousel()
        this.observeChanges()
      }
      this.initCountdown()
      this.setupVisibilityHandling()
    }

    disconnectedCallback () {
      this.cleanup()
    }

    cleanup () {
      this.stopAutoplay()
      this.stopCountdown()
      if (this.embla) {
        this.embla.destroy()
        this.embla = null
      }
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
      if (this.carousel) {
        this.carousel.removeEventListener('mouseenter', this.mouseEnterHandler)
        this.carousel.removeEventListener('mouseleave', this.mouseLeaveHandler)
      }
      if (this.visibilityHandler) {
        document.removeEventListener('visibilitychange', this.visibilityHandler)
        this.visibilityHandler = null
        this.visibilityHandlerAdded = false
      }
    }

    setupVisibilityHandling () {
      if (this.visibilityHandlerAdded) return
      this.visibilityHandlerAdded = true

      this.visibilityHandler = () => {
        if (document.hidden) {
          if (this.carousel) {
            this.stopAutoplay()
          }
          this.stopCountdown()
        } else {
          if (this.carousel) {
            this.startAutoplay()
          }
          this.startCountdown()
        }
      }

      document.addEventListener('visibilitychange', this.visibilityHandler)
    }

    initCarousel () {
      if (this.isInitializing) return
      this.isInitializing = true

      try {
        if (this.embla) {
          this.embla.destroy()
        }

        const options = {
          startIndex: 0,
          loop: true,
          axis: 'y',
          slidesToScroll: 1,
        }

        const viewportNode = this.carousel.querySelector('.announcement-bar__viewport')
        if (!viewportNode) {
          this.isInitializing = false
          return
        }

        this.embla = EmblaCarousel(viewportNode, options)

        this.startAutoplay()

        // Remove existing listeners before adding new ones
        if (this.mouseEnterHandler) {
          this.carousel.removeEventListener('mouseenter', this.mouseEnterHandler)
          this.carousel.removeEventListener('mouseleave', this.mouseLeaveHandler)
        }

        this.mouseEnterHandler = () => this.stopAutoplay()
        this.mouseLeaveHandler = () => this.startAutoplay()

        this.carousel.addEventListener('mouseenter', this.mouseEnterHandler)
        this.carousel.addEventListener('mouseleave', this.mouseLeaveHandler)
      } finally {
        this.isInitializing = false
      }
    }

    initCountdown () {
      if (this.countdownEndTime) {
        this.updateCountdown()
        this.startCountdown()
      }
    }

    startCountdown () {
      if (!this.countdownEndTime) return

      this.stopCountdown()
      this.countdownInterval = setInterval(() => {
        this.updateCountdown()
      }, 1000)
    }

    stopCountdown () {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
        this.countdownInterval = null
      }
    }

    updateCountdown () {
      const countdownElements = this.querySelectorAll('.announcement-bar__countdown')
      if (!countdownElements.length || !this.countdownEndTime) return

      const now = new Date().getTime()
      const endTime = new Date(this.countdownEndTime).getTime()
      const timeLeft = endTime - now

      if (timeLeft <= 0) {
        countdownElements.forEach(element => {
          element.textContent = 'Expired'
        })
        this.stopCountdown()
        return
      }

      const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24))
      const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))

      let displayText = ''
      if (days > 0) {
        displayText = `${days} ${this.daysLabel}`
      } else {
        displayText = `${hours} ${this.hoursLabel}`
      }

      countdownElements.forEach(element => {
        element.textContent = displayText
      })
    }

    startAutoplay () {
      this.stopAutoplay()

      if (this.embla) {
        this.autoplayInterval = setInterval(() => {
          this.embla.scrollNext()
        }, this.autoplaySpeed)
      }
    }

    stopAutoplay () {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval)
        this.autoplayInterval = null
      }
    }

    observeChanges () {
      if (this.observer) return // Prevent multiple observers

      let timeoutId = null

      this.observer = new MutationObserver(() => {
        // Throttle the callback to prevent rapid successive calls
        if (timeoutId) clearTimeout(timeoutId)

        timeoutId = setTimeout(() => {
          // Only reinitialize if not currently initializing and carousel exists
          if (!this.isInitializing && this.carousel) {
            this.initCarousel()
          }
          this.initCountdown()
        }, 100) // 100ms delay to throttle
      })

      this.observer.observe(this, {
        childList: true, // Only watch for added/removed child nodes
        subtree: false, // Don't watch deep changes to prevent triggering on carousel internal changes
      })
    }

    get carousel () {
      return this.querySelector('.announcement-bar__carousel')
    }

    get autoplaySpeed () {
      return parseInt(this.dataset.autoplaySpeed, 10) || 500
    }

    get countdownEndTime () {
      return this.dataset.countdownEnd
    }

    get daysLabel () {
      return this.dataset.countdownDaysLabel || 'days'
    }

    get hoursLabel () {
      return this.dataset.countdownHoursLabel || 'hours'
    }
  }

  customElements.define('announcement-bar', AnnouncementBar)
}
